import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { Line } from '@nivo/line';
import styles from './RetailerStats.module.scss';
import { getDatesArray } from '../../../utils/dateHelper';
import { currencyFormat } from '../../../utils/formatHelper';

class RetailerStats extends PureComponent {
  state = {
    brands: this.props.brands,
    replenish: this.props.replenish,
  };

  static getDerivedStateFromProps(nextProps, prevState) {
    const { brands, replenish, filterRange } = nextProps;
    const brandsFullArray = getDatesArray(brands, filterRange);
    const replenishFullArray = getDatesArray(replenish, filterRange);

    if (
      prevState.filterRange &&
      Date.parse(filterRange.start) === Date.parse(prevState.filterRange.start) &&
      Date.parse(filterRange.end) === Date.parse(prevState.filterRange.end)
    ) {
      return null;
    }
    return {
      brandsSort: brandsFullArray,
      replenishSort: replenishFullArray,
      filterRange,
    };
  }

  getNoDataGraph() {
    const { filterRange } = this.state;
    const x1 = `${filterRange.start.format('dd/mm')}`;
    const x2 = `${filterRange.end.format('dd/mm')}`;
    return [
      {
        id: 'No data',
        data: [
          { x: x1, y: 0 },
          { x: x2, y: 0 },
        ],
      },
    ];
  }

  getDataForIncomeLine() {
    const { brandsSort, replenishSort } = this.state;
    const noSort = !brandsSort || !replenishSort || brandsSort.length === 0 || replenishSort.length === 0;

    if (noSort) {
      return this.getNoDataGraph();
    }

    const mapClosure = (item) => {
      const x = item.date;
      const y = item.total_income;
      return { x, y };
    };

    const brandsLine = brandsSort.map(mapClosure);
    const reorderLine = replenishSort.map(mapClosure);

    const totalLine = brandsLine.map((item, i) => {
      let resultIncome = parseFloat(item.y) + parseFloat(reorderLine[i]['y']);
      return { x: item.x, y: resultIncome };
    });

    return [
      { id: 'Drop Ship orders value', data: brandsLine },
      { id: 'Reorder orders value', data: reorderLine },
      { id: 'Total orders value', data: totalLine },
    ];
  }

  getDataForOrdersLine() {
    const { replenishSort, brandsSort } = this.state;
    const noSort = !brandsSort || !replenishSort || brandsSort.length === 0 || replenishSort.length === 0;

    if (noSort) {
      return this.getNoDataGraph();
    }

    const mapClosure = (item) => {
      return {
        x: item.date,
        y: item.total_orders,
      };
    };

    const reorderLine = replenishSort.map(mapClosure);
    const brandsLine = brandsSort.map(mapClosure);

    const totalLine = brandsLine.map((item, i) => {
      let resultOrders = item.y + reorderLine[i]['y'];
      return {
        x: item.x,
        y: resultOrders,
      };
    });

    return [
      { id: 'Drop Ship orders', data: brandsLine },
      { id: 'Reorder orders', data: reorderLine },
      { id: 'Total orders', data: totalLine },
    ];
  }

  getDataForItemsLine() {
    const { replenishSort, brandsSort } = this.state;
    const noSort = !brandsSort || !replenishSort || brandsSort.length === 0 || replenishSort.length === 0;

    if (noSort) {
      return this.getNoDataGraph();
    }

    const mapClosure = (item) => {
      return {
        x: item.date,
        y: item.total_items,
      };
    };

    const reorderLine = replenishSort.map(mapClosure);
    const brandsLine = brandsSort.map(mapClosure);

    const totalLine = brandsLine.map((item, i) => {
      return {
        x: item.x,
        y: item.y + reorderLine[i]['y'],
      };
    });

    return [
      { id: 'Drop Ship items', data: brandsLine },
      { id: 'Reorder items', data: reorderLine },
      { id: 'Total items', data: totalLine },
    ];
  }

  getDataTotal(chartData, totalDataAxisName) {
    if (!Array.isArray(chartData) || chartData.length === 0) {
      return 0;
    }

    return chartData.find((obj) => obj.id === totalDataAxisName).data.reduce((sum, segment) => sum + segment.y, 0);
  }

  render() {
    const { brands, replenish } = this.state;

    if (!brands.length && !replenish.length) {
      return 'No Data';
    }

    const currency = brands[0] ? brands[0].currency : replenish[0].currency;

    let incomeLineData = this.getDataForIncomeLine();
    let ordersLineData = this.getDataForOrdersLine();
    let itemsLineData = this.getDataForItemsLine();

    const incomeTotal = this.getDataTotal(incomeLineData, 'Total orders value');
    const ordersCountTotal = this.getDataTotal(ordersLineData, 'Total orders');
    const itemsCountTotal = this.getDataTotal(itemsLineData, 'Total items');

    incomeLineData.forEach((dataLine) => {
      dataLine.data.map((item) => {
        item.y = item.y / 100;
        return item;
      });
    });

    const cymbioColors = ['#5c03ec', '#e119b9', '#14133A'];

    return (
      <div className={styles.retailerStats}>
        {brands || replenish ? (
          <div className={styles.container}>
            <div className={styles.income}>
              <h1>{currencyFormat(incomeTotal, currency)}</h1>
              <span>Total orders value</span>
            </div>
            <div className={styles.basket}>
              <h1>{itemsCountTotal}</h1>
              <span>Total items</span>
            </div>
            <div className={styles.pie}>
              <h1>{ordersCountTotal}</h1>
              <span>Total orders</span>
            </div>
          </div>
        ) : null}

        <div className={styles.chartContainer}>
          <div>
            <h3>Orders value</h3>
            <Line
              width={500}
              height={300}
              margin={{
                top: 20,
                right: 20,
                bottom: 60,
                left: 70,
              }}
              curve="monotoneX"
              data={incomeLineData}
              xScale={{ type: 'point' }}
              axisBottom={{ orient: 'bottom', tickRotation: -38 }}
              yScale={{ type: 'linear', stacked: false, min: 0 }}
              enableDotLabel={false}
              colors={cymbioColors}
            />
          </div>
          <div>
            <h3>Orders</h3>
            <Line
              width={500}
              height={300}
              margin={{
                top: 20,
                right: 20,
                bottom: 60,
                left: 70,
              }}
              curve="monotoneX"
              data={ordersLineData}
              animate
              xScale={{ type: 'point' }}
              axisBottom={{ orient: 'bottom', tickRotation: -25 }}
              yScale={{ type: 'linear', stacked: false, min: 0 }}
              enableDotLabel={false}
              colors={cymbioColors}
            />
          </div>
          <div>
            <h3>Items</h3>
            <Line
              width={500}
              height={300}
              margin={{
                top: 20,
                right: 20,
                bottom: 60,
                left: 70,
              }}
              curve="monotoneX"
              data={itemsLineData}
              animate
              xScale={{ type: 'point' }}
              yScale={{ type: 'linear', stacked: false, min: 0 }}
              axisBottom={{ orient: 'bottom', tickRotation: -38 }}
              enableDotLabel={false}
              colors={cymbioColors}
            />
          </div>
        </div>
      </div>
    );
  }
}

export default RetailerStats;

RetailerStats.propTypes = {
  brands: PropTypes.array.isRequired,
  replenish: PropTypes.array.isRequired,
  totalIncome: PropTypes.number.isRequired,
  totalItems: PropTypes.number.isRequired,
  totalOrders: PropTypes.number.isRequired,
};
